import { QuoteStatus } from "~/shared/constants/quote-status.ts";
import type { QuoteBoardItem } from "~/shared/schemas/quote-board-schema.ts";

/**
 * The complete Quote Board status vocabulary. Individual, Bulk Quote and Bulk
 * Tender rows all resolve to one of these, so presentation, click behavior and
 * row capabilities stay in lockstep regardless of how the row was created.
 */
export type QuoteBoardStatus =
	| "under-review"
	| "select-carrier"
	| "details-needed"
	| "ready-to-tender"
	| "processing"
	| "tendered"
	| "expired"
	| "error"
	| "unknown";

export type QuoteBoardRowKind = "individual" | "bulk-quote" | "bulk-tender";

export type QuoteBoardStatusAction =
	| { kind: "navigate"; to: string; withReturnTo: boolean }
	| { kind: "open-carrier-modal" }
	| { kind: "open-error-modal" }
	| { kind: "none" };

export type QuoteBoardStatusDescriptor = {
	status: QuoteBoardStatus;
	rowKind: QuoteBoardRowKind;
	/** Button copy. Already resolved for the current deployment. */
	label: string;
	/** Presentation only — never branch behavior on this. */
	className: string;
	action: QuoteBoardStatusAction;
	/** Row may be included in a Bulk Tender submission. */
	canTender: boolean;
	/** Row may be re-rated from the Quote Board. */
	canRequote: boolean;
};

export type QuoteBoardStatusContext = { isInternal?: boolean };

/**
 * Structural view of the fields status resolution depends on. Accepts both
 * internal and external board rows without forcing callers to widen types.
 */
export type QuoteBoardStatusRow = {
	quoteId: number;
	statusLevel?: string | null;
	statusLevelId?: number | null;
	tabType?: string;
	expirationDate?: string | null;
	bulkQuoteId?: string | null;
	bulkUploadException?: string | null;
	isLtlTender?: boolean | null;
	bulkTenderStatus?: QuoteBoardItem["bulkTenderStatus"];
	bulkTenderReasonCode?: string | null;
};

const STATUS_CLASS_NAMES: Record<QuoteBoardStatus, string> = {
	"under-review": "need-to-review",
	"select-carrier": "carrier-selection",
	"details-needed": "details-needed",
	"ready-to-tender": "ready-for-tender",
	processing: "processing",
	tendered: "tendered",
	expired: "expired",
	error: "error",
	unknown: "",
};

const STATUS_LABELS: Record<QuoteBoardStatus, string> = {
	"under-review": "Under Review",
	"select-carrier": "Select a Carrier",
	"details-needed": "Details Needed",
	"ready-to-tender": "Ready to Tender",
	processing: "Processing",
	tendered: "Tendered",
	expired: "Expired",
	error: "Error",
	unknown: "",
};

/** Overrides applied for internal users; anything absent uses the shared label. */
const INTERNAL_STATUS_LABELS: Partial<Record<QuoteBoardStatus, string>> = {
	"under-review": "Needs Review",
};

const BULK_TENDER_STATUS_MAP: Record<
	NonNullable<QuoteBoardItem["bulkTenderStatus"]>,
	QuoteBoardStatus
> = {
	processing: "processing",
	select_carrier: "select-carrier",
	details_needed: "details-needed",
	ready_to_tender: "ready-to-tender",
	under_review: "under-review",
	tendered: "tendered",
	failed: "error",
};

const LEGACY_STATUS_LEVEL_MAP: Record<string, QuoteBoardStatus> = {
	"Carrier Selection": "select-carrier",
	"Shipment Details": "ready-to-tender",
	Tendered: "tendered",
};

/** Bulk rows that have been committed or hard-failed cannot be re-rated. */
const REQUOTABLE_BULK_TENDER_STATUSES: ReadonlySet<string> = new Set([
	"select_carrier",
	"details_needed",
	"ready_to_tender",
	"under_review",
]);

/** Quote types priced by hand, so the row is reviewed before a carrier exists. */
const REVIEWED_TAB_TYPES: ReadonlySet<string> = new Set(["Spot", "Volume"]);

/** Carrier pricing returned nothing — a review case rather than a retryable failure. */
const NO_CARRIER_RATES_REASON_CODE = "NO_CARRIER_RATES";

const INSURANCE_ADDABLE_STATUSES: ReadonlySet<QuoteBoardStatus> = new Set([
	"select-carrier",
	"details-needed",
	"under-review",
	"ready-to-tender",
]);

const INSURANCE_CONTENT_HIDDEN_STATUSES: ReadonlySet<QuoteBoardStatus> =
	new Set(["error", "under-review"]);

const isExpired = (expirationDate?: string | null): boolean => {
	if (!expirationDate) return false;
	const expiration = new Date(expirationDate);
	return !Number.isNaN(expiration.getTime()) && expiration < new Date();
};

export const getQuoteBoardRowKind = (
	item: Pick<QuoteBoardStatusRow, "bulkQuoteId" | "isLtlTender">,
): QuoteBoardRowKind => {
	if (item.bulkQuoteId == null) return "individual";
	// Both bulk kinds carry a bulkTenderStatus, so only the tender flag separates them.
	return item.isLtlTender === true ? "bulk-tender" : "bulk-quote";
};

const resolveBulkTenderStatus = (
	item: QuoteBoardStatusRow,
	bulkTenderStatus: NonNullable<QuoteBoardItem["bulkTenderStatus"]>,
): QuoteBoardStatus => {
	const status = BULK_TENDER_STATUS_MAP[bulkTenderStatus];
	// Rows persisted before `under_review` existed still report the no-rates case as failed.
	if (
		status === "error" &&
		item.bulkTenderReasonCode === NO_CARRIER_RATES_REASON_CODE
	) {
		return "under-review";
	}
	return status;
};

/**
 * External rows surface Tendered and Expired through `tabType` ahead of the
 * quote's own expiration date, so those cases resolve before the shared rules.
 */
const resolveExternalStatus = (
	item: QuoteBoardStatusRow,
): QuoteBoardStatus | undefined => {
	if (item.statusLevel === "Tendered") return "tendered";
	if (item.tabType === "Expired") return "expired";

	const tabType = item.tabType ?? "";
	if (
		item.statusLevel === "Shipment Details" &&
		(tabType === "Saved" || REVIEWED_TAB_TYPES.has(tabType))
	) {
		return "ready-to-tender";
	}
	if (item.statusLevel === "Carrier Selection") {
		if (tabType === "Saved") return "select-carrier";
		if (REVIEWED_TAB_TYPES.has(tabType)) return "under-review";
	}
	return undefined;
};

const resolveLegacyStatus = (
	item: QuoteBoardStatusRow,
	isInternal: boolean,
): QuoteBoardStatus => {
	if (!item.statusLevel) {
		return !isInternal && item.tabType === "Expired" ? "expired" : "unknown";
	}

	if (!isInternal) {
		const externalStatus = resolveExternalStatus(item);
		if (externalStatus) return externalStatus;
	}

	if (isExpired(item.expirationDate)) return "expired";
	if (
		item.statusLevelId === QuoteStatus.Review ||
		item.statusLevel === "Under Review"
	) {
		return "under-review";
	}
	if (
		item.statusLevel === "Carrier Selection" &&
		REVIEWED_TAB_TYPES.has(item.tabType ?? "")
	) {
		return "under-review";
	}

	return LEGACY_STATUS_LEVEL_MAP[item.statusLevel] ?? "under-review";
};

const resolveStatus = (
	item: QuoteBoardStatusRow,
	isInternal: boolean,
): QuoteBoardStatus => {
	// Bulk Quote and Bulk Tender rows both carry a derived status; only the action differs.
	if (item.bulkTenderStatus) {
		return resolveBulkTenderStatus(item, item.bulkTenderStatus);
	}
	if (item.bulkUploadException) return "error";
	return resolveLegacyStatus(item, isInternal);
};

const getCarrierSelectionPath = (item: QuoteBoardStatusRow): string =>
	item.isLtlTender
		? `/ltl-shipment/carrier-selection/${String(item.quoteId)}`
		: `/carrier-selection/${String(item.quoteId)}`;

/**
 * A non-bulk LTL tender quote is edited entirely inside the LTL tender wizard,
 * so every actionable status keeps the user on that route.
 */
const usesLtlTenderWizard = (
	item: QuoteBoardStatusRow,
	rowKind: QuoteBoardRowKind,
): boolean => rowKind === "individual" && item.isLtlTender === true;

const getShipmentDetailsPath = (
	item: QuoteBoardStatusRow,
	rowKind: QuoteBoardRowKind,
): string => {
	if (rowKind === "bulk-tender") {
		return `/ltl-shipment/${String(item.quoteId)}`;
	}

	return usesLtlTenderWizard(item, rowKind)
		? getCarrierSelectionPath(item)
		: `/shipment-details/${String(item.quoteId)}`;
};

const getExpiredPath = (
	item: QuoteBoardStatusRow,
	rowKind: QuoteBoardRowKind,
): string => {
	if (usesLtlTenderWizard(item, rowKind)) return getCarrierSelectionPath(item);
	return item.statusLevel === "Shipment Details"
		? `/shipment-details/${String(item.quoteId)}`
		: `/carrier-selection/${String(item.quoteId)}`;
};

const getStatusAction = (
	status: QuoteBoardStatus,
	item: QuoteBoardStatusRow,
	rowKind: QuoteBoardRowKind,
	isInternal: boolean,
): QuoteBoardStatusAction => {
	switch (status) {
		case "error": {
			return { kind: "open-error-modal" };
		}
		case "select-carrier":
		case "under-review": {
			return {
				kind: "navigate",
				to: getCarrierSelectionPath(item),
				withReturnTo: false,
			};
		}
		case "expired": {
			return {
				kind: "navigate",
				to: getExpiredPath(item, rowKind),
				withReturnTo: false,
			};
		}
		// Repairing a row sends the user back to the board once it is saved.
		case "details-needed": {
			return {
				kind: "navigate",
				to: getShipmentDetailsPath(item, rowKind),
				withReturnTo: true,
			};
		}
		case "ready-to-tender": {
			return {
				kind: "navigate",
				to: item.isLtlTender
					? getCarrierSelectionPath(item)
					: getShipmentDetailsPath(item, rowKind),
				withReturnTo: false,
			};
		}
		case "tendered": {
			return rowKind === "individual" && isInternal
				? { kind: "navigate", to: "/tender", withReturnTo: false }
				: { kind: "none" };
		}
		case "processing":
		case "unknown": {
			return { kind: "none" };
		}
	}
};

export const resolveQuoteBoardStatus = (
	item: QuoteBoardStatusRow,
	context: QuoteBoardStatusContext = {},
): QuoteBoardStatusDescriptor => {
	const { isInternal = false } = context;
	const rowKind = getQuoteBoardRowKind(item);
	const status = resolveStatus(item, isInternal);
	const bulkTenderStatus = item.bulkTenderStatus;

	return {
		status,
		rowKind,
		label:
			(isInternal ? INTERNAL_STATUS_LABELS[status] : undefined) ??
			STATUS_LABELS[status],
		className: STATUS_CLASS_NAMES[status],
		action: getStatusAction(status, item, rowKind, isInternal),
		canTender: rowKind !== "individual" && status === "ready-to-tender",
		canRequote:
			rowKind !== "individual" &&
			bulkTenderStatus != null &&
			REQUOTABLE_BULK_TENDER_STATUSES.has(bulkTenderStatus),
	};
};

/**
 * Destination for the Quote ID link. Mirrors the status action so the two cells
 * cannot drift, falling back to Carrier Selection when the status opens a modal.
 */
export const getQuoteBoardQuoteUrl = (
	item: QuoteBoardStatusRow,
	context: QuoteBoardStatusContext = {},
): string => {
	const { action } = resolveQuoteBoardStatus(item, context);
	if (action.kind === "navigate") return action.to;
	if (action.kind === "open-carrier-modal")
		return getCarrierSelectionPath(item);
	return "";
};

export const canAddInsuranceQuoteBoardRow = (
	item: QuoteBoardStatusRow,
	context?: QuoteBoardStatusContext,
): boolean => {
	const { rowKind, status } = resolveQuoteBoardStatus(item, context);
	return rowKind === "bulk-tender" && INSURANCE_ADDABLE_STATUSES.has(status);
};

export const shouldHideQuoteBoardInsuranceCell = (
	item: QuoteBoardStatusRow,
	context?: QuoteBoardStatusContext,
): boolean => {
	const { rowKind, status } = resolveQuoteBoardStatus(item, context);
	return (
		rowKind === "bulk-quote" ||
		(rowKind === "individual" && INSURANCE_CONTENT_HIDDEN_STATUSES.has(status))
	);
};

export const canTenderQuoteBoardRow = (
	item: QuoteBoardStatusRow,
	context?: QuoteBoardStatusContext,
): boolean => resolveQuoteBoardStatus(item, context).canTender;

export const canRequoteQuoteBoardRow = (
	item: QuoteBoardStatusRow,
	context?: QuoteBoardStatusContext,
): boolean => resolveQuoteBoardStatus(item, context).canRequote;

/**
 * Copy for the Carrier Name cell's board-level carrier modal action.
 * `change` replaces an existing selection, `select` makes the first one.
 */
export type QuoteBoardCarrierAction = "change" | "select" | "none";

/**
 * Bulk rows with a carrier can swap it without leaving the board (`change`).
 * Only Bulk Tender rows still awaiting a carrier offer `select`.
 */
export const getQuoteBoardCarrierAction = (
	item: QuoteBoardStatusRow & { carrierName?: string | null },
	context?: QuoteBoardStatusContext,
): QuoteBoardCarrierAction => {
	if (item.bulkQuoteId == null) return "none";
	if (item.bulkTenderStatus === "tendered" || item.statusLevel === "Tendered") {
		return "none";
	}
	if (item.carrierName) return "change";
	const { rowKind, status } = resolveQuoteBoardStatus(item, context);
	return rowKind === "bulk-tender" && status === "select-carrier"
		? "select"
		: "none";
};

export const isQuoteBoardSelectionEligible = (
	item: QuoteBoardStatusRow,
	context?: QuoteBoardStatusContext,
): boolean => {
	const { canTender, canRequote } = resolveQuoteBoardStatus(item, context);
	return canTender || canRequote;
};
